// eslint-disable-next-line no-unused-vars
import React, { useState, useMemo, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  BookOpen,
  Clock,
  ArrowUpRight,
  CheckCircle,
  GraduationCap,
  Truck,
  Package,
  AlertCircle,
  ShoppingBag,
  MoreHorizontal,
} from "lucide-react";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  Cell,
  Tooltip,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  PieChart,
  Pie,
} from "recharts";
import { AdminContext } from "../../../context/AdminContext";

const typeMap = {
  short: "Short Term Training",
  medium: "Medium Term Training",
  professional: "Professional Training",
};

const statusMap = {
  new: "New",
  inprogress: "In Progress",
  completed: "Completed",
};

const BTPDashboard = () => {
  const navigate = useNavigate();
  const { trainings, bookPurchases, fetchAllTrainings, fetchAllBookPurchases } =
    useContext(AdminContext);
  const [activeFilter, setActiveFilter] = useState("Last Month");
  const filters = ["Last 3 Days", "Last Week", "Last Month", "Last Year"];

  useEffect(() => {
    fetchAllTrainings();
    fetchAllBookPurchases();
  }, []);

  const getFilterDays = (filter) => {
    switch (filter) {
      case "Last 3 Days":
        return 3;
      case "Last Week":
        return 7;
      case "Last Month":
        return 30;
      case "Last Year":
        return 365;
      default:
        return 30;
    }
  };

  const dashboardData = useMemo(() => {
    const days = getFilterDays(activeFilter);
    const currentDate = new Date();
    const filterDate = new Date(
      currentDate.getTime() - days * 24 * 60 * 60 * 1000,
    );

    const filteredTrainings = trainings.filter(
      (t) => new Date(t.createdAt) >= filterDate,
    );
    const filteredBookPurchases = bookPurchases.filter(
      (b) => new Date(b.createdAt) >= filterDate,
    );

    // Training stats
    const trainingShort = filteredTrainings.filter(
      (t) => t.trainingType === "short",
    ).length;
    const trainingMedium = filteredTrainings.filter(
      (t) => t.trainingType === "medium",
    ).length;
    const trainingProfessional = filteredTrainings.filter(
      (t) => t.trainingType === "professional",
    ).length;

    const trainingNew = filteredTrainings.filter(
      (t) => t.status === "new",
    ).length;
    const trainingInProgress = filteredTrainings.filter(
      (t) => t.status === "inprogress",
    ).length;
    const trainingCompleted = filteredTrainings.filter(
      (t) => t.status === "completed",
    ).length;

    // Book stats
    const totalOrders = filteredBookPurchases.length;
    const revenue = filteredBookPurchases
      .reduce(
        (acc, b) => acc + (Number(b.amount) || Number(b.totalAmount) || 0),
        0,
      )
      .toFixed(2);

    const revenueByDay = filteredBookPurchases.reduce((acc, b) => {
      const day = new Date(b.createdAt).toLocaleDateString();
      acc[day] = (acc[day] || 0) + b.totalAmount;
      return acc;
    }, {});
    const revenueTrend = Object.entries(revenueByDay)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => new Date(a.name) - new Date(b.name));

    const delivered = filteredBookPurchases.filter(
      (b) => b.status === "delivered",
    ).length;
    const pending = filteredBookPurchases.filter(
      (b) => b.status === "pending",
    ).length;
    const standard = filteredBookPurchases.filter(
      (b) => b.deliveryOption === "standard",
    ).length;
    const pickup = filteredBookPurchases.filter(
      (b) => b.deliveryOption === "pickup",
    ).length;

    // Recent activities
    const recentActivities = filteredTrainings
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 5)
      .map((t) => ({
        id: t.id,
        user: {
          name: t.user.name,
          email: t.user.phone || "N/A",
          avatar:
            t.user.image ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(
              t.user.name,
            )}&background=random`,
        },
        sessionType: typeMap[t.trainingType] || t.trainingType,
        participants: t.numberOfCaregivers || 1,
        date: new Date(t.createdAt).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
        status: statusMap[t.status] || t.status,
      }));

    return {
      training: {
        totalEnrollments: filteredTrainings.length,
        byType: [
          { name: "Short Term", value: trainingShort, fill: "#10b981" },
          { name: "Medium Term", value: trainingMedium, fill: "#3b82f6" },
          {
            name: "Professional",
            value: trainingProfessional,
            fill: "#8b5cf6",
          },
        ],
        statusData: [
          { name: "Completed", value: trainingCompleted, fill: "#10b981" },
          { name: "In Progress", value: trainingInProgress, fill: "#f59e0b" },
          { name: "New", value: trainingNew, fill: "#6366f1" },
        ],
      },
      books: {
        totalOrders,
        revenue,
        revenueTrend:
          revenueTrend.length > 0 ? revenueTrend : [{ name: "P1", value: 0 }],
        delivery: {
          delivered,
          pending,
          standard,
          pickup,
        },
      },
      recentActivities,
    };
  }, [activeFilter, trainings, bookPurchases]);

  const COLORS = ["#10b981", "#f59e0b", "#6366f1"];

  const handleViewAllClick = () => {
    navigate("/btp/training-requests");
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12 space-y-8">
      {/* Header & Filter */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-text dark:text-dark-text">
            BTP Dashboard
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1 font-medium">
            Training programs & book sales analytics.
          </p>
        </div>

        <div className="bg-card dark:bg-dark-surface p-1 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border flex flex-wrap gap-1 w-full sm:w-auto">
          {filters.map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all flex-1 sm:flex-none min-w-[80px] sm:min-w-0 ${
                activeFilter === filter
                  ? "bg-primary text-white dark:bg-primary dark:text-white shadow-md"
                  : "text-gray-500 hover:bg-accent dark:hover:bg-gray-700"
              }`}
            >
              {filter}
            </button>
          ))}
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* -- TRAINING SECTION -- */}

        {/* Training Enrollments  */}
        <div className="lg:col-span-5 bg-slate-900 rounded-[2rem] p-4 sm:p-6 shadow-sm border border-slate-800 relative overflow-hidden group">
          <div className="flex justify-between items-start mb-6">
            <div>
              <p className="text-gray-400 font-bold text-xs uppercase tracking-wide">
                Total Enrollments
              </p>
              <h3 className="text-3xl sm:text-4xl font-extrabold text-white mt-1">
                {dashboardData.training.totalEnrollments}
              </h3>
            </div>
            <div className="text-emerald-400">
              <GraduationCap size={24} />
            </div>
          </div>

          <div className="h-32 sm:h-40 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={dashboardData.training.byType}
                margin={{ top: 10, right: 0, left: -20, bottom: 0 }}
              >
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                />
                <Tooltip
                  cursor={{ fill: "transparent" }}
                  contentStyle={{
                    borderRadius: "12px",
                    border: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    backgroundColor: "#ffffff",
                    color: "#111827",
                  }}
                />
                <Bar dataKey="value" radius={[6, 6, 6, 6]} barSize={30}>
                  {dashboardData.training.byType.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Training Status (Medium Card) */}
        <div className="lg:col-span-3 bg-slate-900 rounded-[2rem] p-4 sm:p-6 shadow-sm border border-slate-800 flex flex-col justify-between">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-bold text-white">Session Status</h4>
            <MoreHorizontal size={18} className="text-gray-400" />
          </div>

          <div className="relative h-32 sm:h-40 w-full flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={dashboardData.training.statusData}
                  innerRadius={30}
                  outerRadius={50}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {dashboardData.training.statusData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    borderRadius: "12px",
                    border: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    backgroundColor: "#ffffff",
                    color: "#111827",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Center Text */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <span className="block text-lg sm:text-xl font-bold text-white">
                  {dashboardData.training.totalEnrollments}
                </span>
                <span className="text-[10px] text-gray-400 uppercase">
                  Total
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-2 mt-2">
            {dashboardData.training.statusData.map((item, index) => (
              <div
                key={item.name}
                className="flex justify-between items-center text-xs sm:text-sm"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: COLORS[index] }}
                  ></div>
                  <span className="text-gray-400">{item.name}</span>
                </div>
                <span className="font-bold text-white">{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* -- BOOK PURCHASE SECTION -- */}

        {/* Book Revenue (Large Card) */}
        <div className="lg:col-span-4 bg-slate-900 rounded-[2rem] p-4 sm:p-6 border border-slate-800 relative overflow-hidden">
          <div className="flex justify-between items-start z-10 relative">
            <div>
              <p className="text-gray-400 font-bold text-xs uppercase tracking-wide">
                Total Revenue
              </p>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-white mt-1">
                ETB {dashboardData.books.revenue}
              </h3>
              <div className="flex items-center gap-2 mt-2">
                <span className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded-lg text-xs font-bold flex items-center gap-1">
                  <ShoppingBag size={12} /> {dashboardData.books.totalOrders}{" "}
                  Orders
                </span>
              </div>
            </div>
            <div className="text-yellow-400">
              <BookOpen size={24} />
            </div>
          </div>

          <div className="h-28 sm:h-32 mt-6">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dashboardData.books.revenueTrend}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#fbbf24" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#fbbf24" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#fbbf24"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorRevenue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Book Logistics (Full Width Grid Item) */}
        <div className="lg:col-span-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Delivery Stats Cards */}
          <div className="bg-slate-900 p-4 sm:p-5 rounded-2xl border border-slate-800 shadow-sm flex items-center gap-4">
            <div className="text-green-400">
              <CheckCircle size={20} className="sm:w-6 sm:h-6" />
            </div>
            <div>
              <p className="text-gray-400 text-xs font-bold uppercase">
                Delivered
              </p>
              <h4 className="text-xl sm:text-2xl font-bold text-white">
                {dashboardData.books.delivery.delivered}
              </h4>
            </div>
          </div>

          <div className="bg-slate-900 p-4 sm:p-5 rounded-2xl border border-slate-800 shadow-sm flex items-center gap-4">
            <div className="text-orange-400">
              <Clock size={20} className="sm:w-6 sm:h-6" />
            </div>
            <div>
              <p className="text-gray-400 text-xs font-bold uppercase">
                Pending
              </p>
              <h4 className="text-xl sm:text-2xl font-bold text-white">
                {dashboardData.books.delivery.pending}
              </h4>
            </div>
          </div>

          <div className="bg-slate-900 p-4 sm:p-5 rounded-2xl border border-slate-800 shadow-sm flex items-center gap-4">
            <div className="text-blue-400">
              <Truck size={20} className="sm:w-6 sm:h-6" />
            </div>
            <div>
              <p className="text-gray-400 text-xs font-bold uppercase">
                Standard Delivery
              </p>
              <h4 className="text-xl sm:text-2xl font-bold text-white">
                {dashboardData.books.delivery.standard}
              </h4>
            </div>
          </div>

          <div className="bg-slate-900 p-4 sm:p-5 rounded-2xl border border-slate-800 shadow-sm flex items-center gap-4">
            <div className="text-purple-400">
              <Package size={20} className="sm:w-6 sm:h-6" />
            </div>
            <div>
              <p className="text-gray-400 text-xs font-bold uppercase">
                Pickup
              </p>
              <h4 className="text-xl sm:text-2xl font-bold text-white">
                {dashboardData.books.delivery.pickup}
              </h4>
            </div>
          </div>
        </div>

        {/* Recent Activities Table */}
        <div className="lg:col-span-12 bg-card dark:bg-dark-surface rounded-[2rem] border border-gray-100 dark:border-dark-border shadow-sm overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h3 className="text-lg font-bold text-text dark:text-dark-text">
                Recent Training Registrations
              </h3>
              <p className="text-sm text-gray-500 dark:text-dark-muted">
                Latest 5 active registrations
              </p>
            </div>
            <button
              onClick={handleViewAllClick}
              className="text-sm font-bold text-primary hover:text-primary/80 flex items-center gap-1 cursor-pointer"
            >
              View All <ArrowUpRight size={16} />
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-900 text-xs text-gray-400 uppercase font-semibold">
                <tr>
                  <th className="px-4 sm:px-6 py-3">User</th>
                  <th className="px-4 sm:px-6 py-3">Session Type</th>
                  <th className="px-4 sm:px-6 py-3">Participants</th>
                  <th className="px-4 sm:px-6 py-3">Date</th>
                  <th className="px-4 sm:px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {dashboardData.recentActivities.map((activity) => (
                  <tr
                    key={activity.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/20 transition-colors cursor-pointer"
                    onClick={() => navigate("/btp/training-requests")}
                  >
                    <td className="px-4 sm:px-6 py-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={activity.user.avatar}
                          alt={activity.user.name}
                          className="hidden sm:block w-8 h-8 rounded-full object-cover"
                        />
                        <div>
                          <p className="font-bold text-text dark:text-dark-text">
                            {activity.user.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-dark-muted">
                            {activity.user.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-3 font-medium text-gray-700 dark:text-dark-muted">
                      <span className="hidden sm:inline">
                        {activity.sessionType}
                      </span>
                      <span className="sm:hidden text-xs">
                        {activity.sessionType.split(" ")[0]}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-3">
                      <div className="flex items-center gap-1.5">
                        <Users size={14} className="text-gray-400" />
                        <span className="text-text dark:text-dark-text font-bold">
                          {activity.participants}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-gray-500 dark:text-dark-muted">
                      <span className="hidden sm:inline">{activity.date}</span>
                      <span className="sm:hidden text-xs">
                        {activity.date.split(",")[0]}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-3">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${
                          activity.status === "Completed"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-800"
                            : activity.status === "In Progress"
                              ? "bg-yellow-50 text-yellow-700 border-yellow-100 dark:bg-yellow-900/20 dark:border-yellow-800"
                              : "bg-blue-50 text-blue-700 border-blue-100 dark:bg-blue-900/20 dark:border-blue-800"
                        }`}
                      >
                        {activity.status === "Completed" && (
                          <CheckCircle size={12} />
                        )}
                        {activity.status === "In Progress" && (
                          <Clock size={12} />
                        )}
                        {activity.status === "New" && <AlertCircle size={12} />}
                        <span className="hidden sm:inline">
                          {activity.status}
                        </span>
                        <span className="sm:hidden">
                          {activity.status === "Completed"
                            ? "Done"
                            : activity.status === "In Progress"
                              ? "Progress"
                              : "New"}
                        </span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BTPDashboard;
